import { useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useUpdateSettings } from '../hooks/useStandards';
import { Portal } from './Portal';

/**
 * The first thing she sees the year after a roll-over.
 *
 * The archive advances the dates by exactly a year so nothing is left blank,
 * but a guess is not a school calendar — the first day back moves, and the
 * last day moves with the snow days. So the year is not really started until
 * a person has looked at those two dates and said yes.
 *
 * Deliberately not dismissable. Everything from the roster to the seating
 * chart hangs off which year it is, and a teacher who skips this and starts
 * adding children has put them in a year that is off by a fortnight. There is
 * nothing to lose by asking now: her old year is already safely archived, and
 * confirming is one button.
 */
export function NewYearPrompt() {
  const { user } = useAuth();
  const update = useUpdateSettings();

  const suggestedStart = user?.settings.schoolYearStart?.slice(0, 10) ?? '';
  const suggestedEnd = user?.settings.schoolYearEnd?.slice(0, 10) ?? '';

  const [start, setStart] = useState(suggestedStart);
  const [end, setEnd] = useState(suggestedEnd);
  const [error, setError] = useState<string | null>(null);

  if (!user?.settings.schoolYearPending) return null;

  const label = start && end ? `${start.slice(0, 4)}–${end.slice(0, 4)}` : 'the new year';

  async function save(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!start || !end) return setError('Both dates are needed.');
    if (end <= start) return setError('The last day has to come after the first.');

    try {
      await update.mutateAsync({
        schoolYearStart: start,
        schoolYearEnd: end,
        schoolYearPending: false,
      });
    } catch {
      setError('That could not be saved. Try again in a moment.');
    }
  }

  return (
    <Portal>
      {/*
        not-dismissable: this one traps the page on purpose. Everything from
        the roster to the seating chart hangs off which school year it is, and
        a teacher who clicks past this and starts adding children has put them
        in a year that is off by a fortnight. It costs one button press.
      */}
      <div
        className="fixed inset-0 z-[90] flex items-center justify-center bg-espresso/50 p-4"
        role="dialog"
        aria-modal="true"
        aria-label="Set the new school year"
      >
        <form
          onSubmit={(e) => void save(e)}
          className="w-full max-w-md rounded-card bg-cream p-6 shadow-lifted"
        >
          <h2 className="text-xl">Welcome back</h2>

          <p className="mt-2 text-sm leading-relaxed text-walnut">
            Last year has been archived — the roster, the seating and every score are put away
            safely and you can look through them whenever you like. Nothing else was touched: your
            lesson plans, notes, to-dos, calendar and bookmarks are all where you left them.
          </p>

          <p className="mt-3 text-sm leading-relaxed text-walnut">
            Set the first and last day of {label} to get started. The last day is when everything
            will be archived again, so it is worth checking against the district calendar.
          </p>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="label">First day</span>
              <input
                type="date"
                className="field"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                required
                autoFocus
              />
            </label>

            <label className="block">
              <span className="label">Last day</span>
              <input
                type="date"
                className="field"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                required
              />
            </label>
          </div>

          {error && <p className="mt-2 text-xs text-rust">{error}</p>}

          <p className="mt-3 text-[11px] leading-relaxed text-stone">
            Both dates can be changed later under Settings → School year.
          </p>

          <button type="submit" className="btn-primary mt-4 w-full" disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Start the year'}
          </button>
        </form>
      </div>
    </Portal>
  );
}
